// src/pages/AmazonItemCostPage.tsx
//
// Amazon 商品原価ページ。楽天版 (MonthlyItemCostPage) と同じ操作感・画面構成に揃える。
// - 共通テーブル (MonthlyItemCostTable) を流用。キーは SKU（損益の cogs 結合キー）、ASIN は補助表示
// - 新規登録は楽天と同じ「表の末尾に常時ある空行（黄色）」に入力する方式（＋ボタンは置かない）
// - 操作バーも楽天版の配置に合わせる（左: 年切替 / 右: CSV系 → 保存）
// - 平均原価率（月別）: SKU 原価が未登録の商品は 売上 × 平均原価率 で概算計上される

import React, { useCallback, useEffect, useRef, useState } from "react";
import { MonthlyItemCostTable } from "../components/MonthlyItemCostTable";
import LoadingInline from "../components/LoadingInline";
import { EditableRow } from "../types/EditableRow";
import { ToastViewport, useToast } from "../components/Toast";
import { apiGet, apiGetJson, apiPostJson, apiPutJson } from "../utils/apiClient";

type MonthDef = { ym: string; label: string };

const PAGE_SIZE = 100;

function buildYearMonths(year: number): MonthDef[] {
    const out: MonthDef[] = [];
    for (let m = 1; m <= 12; m += 1) {
        const mm = String(m).padStart(2, "0");
        out.push({ ym: `${year}-${mm}`, label: `${year}/${mm}` });
    }
    return out;
}

// 0〜1 の率 → 表示用 "%"（例 0.35 → "35"）
function rate01ToView(rate: number | null): string {
    if (rate === null || rate === undefined || !Number.isFinite(rate)) return "";
    return String(Math.round(rate * 1000) / 10);
}

// 表示用 "%" → 0〜1（例 "35" → 0.35）
function viewToRate01(view: string): number | null {
    const trimmed = view.trim();
    if (trimmed === "") return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num < 0) return null;
    return num / 100;
}

function makeClientRowId(prefix: string): string {
    return `amz_${prefix}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

// 楽天版 makeNewEmptyRow と同型。表の末尾に常時置く新規入力用の空行
function makeNewEmptyRow(): EditableRow {
    return {
        client_row_id: makeClientRowId("new"),
        cost: null,
        isNew: true,
        item_code: "",
        item_key: "__new__",
        monthly_costs: Array(12).fill(null),
        old_item_key: "",
        sku_code: "",
    } as any;
}

// 楽天版 ensureNewRowIfNeeded と同型。空行が無ければ末尾に足す
function ensureNewRowIfNeeded(list: EditableRow[]): EditableRow[] {
    const hasNew = list.some((r) => r.item_key === "__new__");
    return hasNew ? list : [...list, makeNewEmptyRow()];
}

type CoverageSummary = {
    year_month: string;
    total_items: number;
    registered_items: number;
    unregistered_items: number;
    registration_rate: number;
    unregistered_details: {
        item_code: string;
        sku_code: string;
        item_key: string;
    }[];
};

const EMPTY_COVERAGE: CoverageSummary = {
    registered_items: 0,
    registration_rate: 0,
    total_items: 0,
    unregistered_details: [],
    unregistered_items: 0,
    year_month: "",
};

export default function AmazonItemCostPage() {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [rows, setRows] = useState<EditableRow[]>([]);
    const [avgCostRatesView, setAvgCostRatesView] = useState<string[]>(Array(12).fill(""));
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [keyword, setKeyword] = useState("");
    const [keywordInput, setKeywordInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    /** ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す（楽天版と同じ） */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /** ✅ 未保存ガード：年切替・ページ切替の前に確認（楽天版と同じ） */
    const confirmDiscardIfDirty = () => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    };
    const [error, setError] = useState<string | null>(null);
    const { toasts, pushToast, removeToast } = useToast();
    const [csvMonth, setCsvMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const loadTokenRef = useRef(0);

    const [coverageYearMonth, setCoverageYearMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const [coverageSummary, setCoverageSummary] = useState<CoverageSummary>(EMPTY_COVERAGE);
    const [coverageLoading, setCoverageLoading] = useState(false);

    const months = buildYearMonths(year);

    const loadCoverageSummary = useCallback(async () => {
        setCoverageLoading(true);
        try {
            const data = await apiGetJson<CoverageSummary>(
                `/api/amazon/item-costs/coverage-summary?year_month=${encodeURIComponent(coverageYearMonth)}`,
            );
            setCoverageSummary({
                registered_items: Number(data?.registered_items ?? 0),
                registration_rate: Number(data?.registration_rate ?? 0),
                total_items: Number(data?.total_items ?? 0),
                unregistered_details: Array.isArray(data?.unregistered_details)
                    ? data.unregistered_details.map((x) => ({
                          item_code: String(x?.item_code ?? ""),
                          item_key: String(x?.item_key ?? ""),
                          sku_code: String(x?.sku_code ?? ""),
                      }))
                    : [],
                unregistered_items: Number(data?.unregistered_items ?? 0),
                year_month: String(data?.year_month ?? `${coverageYearMonth}-01`),
            });
        } catch (e) {
            // 取得できなくても画面を壊さない（楽天版と同じ方針）
        } finally {
            setCoverageLoading(false);
        }
    }, [coverageYearMonth]);

    useEffect(() => {
        void loadCoverageSummary();
    }, [loadCoverageSummary]);

    const downloadUnregisteredCsv = async () => {
        try {
            const res = await apiGet(
                `/api/amazon/item-costs/unregistered-csv?year_month=${encodeURIComponent(coverageYearMonth)}`,
            );
            if (!res.ok) throw new Error(String(res.status));
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `amz_unregistered_items_${coverageYearMonth}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            setError("未登録商品CSVのダウンロードに失敗しました。");
        }
    };

    const load = useCallback(async () => {
        const token = ++loadTokenRef.current;
        setLoading(true);
        setError(null);
        try {
            const url =
                `/api/amazon/item-costs/range?year_month=${year}-01-01` +
                `&page=${page}&pageSize=${PAGE_SIZE}&keyword=${encodeURIComponent(keyword)}`;
            const data = await apiGetJson<{
                avg_cost_rates?: (number | null)[];
                items: Array<{
                    item_code: string;
                    item_key: string;
                    monthly_costs: (number | null)[];
                    sku_code: string;
                }>;
                total: number;
            }>(url);
            if (token !== loadTokenRef.current) return;

            setTotal(Number(data.total ?? 0));
            setAvgCostRatesView(
                Array.from({ length: 12 }, (_, i) => rate01ToView(data.avg_cost_rates?.[i] ?? null)),
            );
            const list: EditableRow[] = (data.items ?? []).map((r) => ({
                client_row_id: makeClientRowId("loaded"),
                cost: null,
                item_code: r.item_code ?? "",
                item_key: r.item_key,
                monthly_costs: Array.isArray(r.monthly_costs)
                    ? r.monthly_costs
                    : Array(12).fill(null),
                old_item_key: r.item_key,
                sku_code: r.sku_code ?? "",
            }));
            setRows(ensureNewRowIfNeeded(list));
            setDirty(false);
        } catch (e) {
            if (token === loadTokenRef.current) {
                setError("商品原価の読み込みに失敗しました。再読み込みしてください。");
            }
        } finally {
            if (token === loadTokenRef.current) setLoading(false);
        }
    }, [year, page, keyword]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleChange = (id: string, field: keyof EditableRow, value: any) => {
        setRows((prev) =>
            ensureNewRowIfNeeded(
                prev.map((r) => (r.client_row_id === id ? { ...r, [field]: value } : r)),
            ),
        );
        setDirty(true);
    };

    const handleChangeAvgCostRate = (idx: number, value: string) => {
        setAvgCostRatesView((prev) => {
            const next = [...prev];
            next[idx] = value;
            return next;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await apiPutJson("/api/amazon/item-costs", {
                avg_cost_rates: avgCostRatesView.map(viewToRate01),
                items: rows.map((r) => ({
                    item_code: r.item_code,
                    // 新規行（__new__）は入力された SKU をキーにする。既存行はロード時の SKU 固定
                    item_key:
                        r.item_key === "__new__" ? (r.sku_code || "").trim() : r.item_key,
                    monthly_costs: (r.monthly_costs ?? []).slice(0, 12),
                    sku_code: r.sku_code,
                })),
                months: months.map((m) => m.ym),
                year_month: `${year}-01-01`,
            });
            pushToast("success", "保存しました");
            await load();
            await loadCoverageSummary();
        } catch (e) {
            setError("保存に失敗しました。もう一度お試しください。");
        } finally {
            setSaving(false);
        }
    };

    const handleTemplateDownload = async () => {
        try {
            const res = await apiGet(
                `/api/amazon/item-costs/template-csv?year_month=${csvMonth}-01`,
            );
            if (!res.ok) throw new Error(String(res.status));
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `amz_item_cost_template_${csvMonth}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            setError("テンプレートのダウンロードに失敗しました。");
        }
    };

    const handleImport = async (file: File) => {
        setImporting(true);
        setError(null);
        try {
            const text = await file.text();
            const json = await apiPostJson<{ error?: string; ok?: boolean; saved?: number }>(
                "/api/amazon/item-costs/import",
                { csv: text },
            );
            if (!json?.ok) {
                throw new Error(json?.error || "取り込みに失敗しました");
            }
            pushToast("success", `CSVインポート完了（${json.saved ?? 0}件）`, 6000);
            await load();
        } catch (e: any) {
            setError(`CSVインポートに失敗しました：${e?.message ?? ""}`);
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-3">
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {/* 未保存ヒント（閉じるまで常駐・楽天版と同じ） */}
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                            <div className="text-xs text-amber-900">変更があります。右上の「保存」を押してください。</div>
                            <button
                                className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                                onClick={() => setDirtyHintDismissed(true)}
                                aria-label="閉じる"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </ToastViewport>
            {/* 登録状況（楽天版と同じ構成） */}
            <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[15px] font-bold text-gray-900">登録状況（売上実績商品）</div>
                    <div className="mt-1 text-[11px] text-gray-500">
                        対象年月: {coverageSummary.year_month ? coverageSummary.year_month.slice(0, 7).replace("-", "/") : "-"}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-[11px] text-gray-600">チェック対象年月</label>
                    <input
                        type="month"
                        className="border px-2 py-1 rounded bg-white text-xs h-8"
                        value={coverageYearMonth}
                        onChange={(e) => setCoverageYearMonth(e.target.value)}
                        disabled={coverageLoading}
                    />
                </div>
            </div>

            <div className="mb-7 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-1 text-[11px] font-medium text-gray-400">売上実績商品の登録数</div>
                    <div className="leading-none">
                        <span className="text-[28px] font-semibold text-gray-900">
                            {coverageLoading ? "-" : coverageSummary.registered_items}
                        </span>
                        <span className="ml-1 text-[14px] font-medium text-gray-400">
                            / {coverageLoading ? "-" : coverageSummary.total_items}
                        </span>
                    </div>
                    <div className="mt-3 h-[4px] overflow-hidden rounded bg-gray-200">
                        <div
                            className="h-full rounded bg-blue-500"
                            style={{
                                width: `${Math.max(0, Math.min(100, coverageSummary.registration_rate || 0))}%`,
                            }}
                        />
                    </div>
                    <div className="mt-2 text-[11px] text-gray-500">
                        売上実績商品に対する登録率 {coverageLoading ? "-" : `${Math.round(coverageSummary.registration_rate)}%`}
                    </div>
                    <div className="mt-1 text-[10px] italic text-gray-400">
                        ※対象年月の売上実績商品にて計算
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-1 text-[11px] font-medium text-gray-400">未登録商品数</div>
                    <div className="leading-none text-[28px] font-semibold text-amber-600">
                        {coverageLoading ? "-" : coverageSummary.unregistered_items}
                    </div>
                    <div className="mt-1 text-[10px] italic text-gray-400">
                        ※対象年月の売上実績商品にて計算
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-1 text-[11px] font-medium text-gray-400">登録率</div>
                    <div className="leading-none text-[28px] font-semibold text-green-600">
                        {coverageLoading ? "-" : `${Math.round(coverageSummary.registration_rate)}%`}
                    </div>
                    <div className="mt-3 text-[11px] text-gray-500">一致している割合</div>
                    <div className="mt-1 text-[10px] italic text-gray-400">
                        ※対象年月の売上実績商品にて計算
                    </div>
                </div>
            </div>

            {coverageSummary.unregistered_details.length > 0 && (
                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="mb-2 text-sm font-semibold text-amber-800">未登録商品一覧</div>
                        <button
                            type="button"
                            onClick={downloadUnregisteredCsv}
                            className="px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-50 text-xs text-amber-800"
                            title="未登録商品のASIN・SKUが入った状態のCSVをダウンロードします。原価を入力して一括アップロードに使えます。"
                        >
                            CSVダウンロード（インポート用）
                        </button>
                    </div>
                    <div className="mb-3 text-xs text-amber-700">
                        未登録商品一覧には、当月に売上があった原価未登録の商品が表示されます。そのため、表示される商品は毎月の売上状況に応じて変動いたします。
                        <br />
                        個別原価は未登録です。平均原価率が設定されている場合は、その値で利益計算されます。
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
                        <table className="min-w-full text-sm">
                            <thead className="bg-amber-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-amber-800">ASIN</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-amber-800">SKU</th>
                                </tr>
                            </thead>
                            <tbody>
                                {coverageSummary.unregistered_details.map((item) => (
                                    <tr key={item.item_key} className="border-t border-amber-100">
                                        <td className="px-3 py-2 text-xs text-gray-700">{item.item_code}</td>
                                        <td className="px-3 py-2 text-xs text-gray-700">{item.sku_code}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-[10px] text-amber-700">※表示上限は100件です</div>
                </div>
            )}

            {/* 操作バー（楽天版と同じ構成: 左=年切替 / 右=CSV系→保存） */}
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">商品原価（Amazon / 12ヶ月）</div>

                    <label className="text-xs text-gray-600 ml-2">年</label>
                    <select
                        className="border px-2 py-1 rounded bg-white text-sm h-8"
                        value={year}
                        onChange={(e) => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                            <option key={y} value={y}>
                                {y}年
                            </option>
                        ))}
                    </select>

                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((prev) => prev - 1);
                            setPage(1);
                        }}
                        title="前年へ"
                    >
                        ← 前年
                    </button>
                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((prev) => prev + 1);
                            setPage(1);
                        }}
                        title="翌年へ"
                    >
                        翌年 →
                    </button>
                </div>

                <div className="flex flex-col items-end gap-2">
                    {/* 1段目: CSV系 */}
                    <div className="flex gap-2 flex-wrap justify-end items-center">
                        <label className="text-[11px] text-gray-600">CSV対象月</label>
                        <input
                            type="month"
                            value={csvMonth}
                            onChange={(e) => setCsvMonth(e.target.value)}
                            className="border px-2 py-1 rounded bg-white text-xs h-7"
                        />
                        <button
                            type="button"
                            onClick={handleTemplateDownload}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-xs"
                            title="販売実績のあるSKUが入った状態のCSVをダウンロードします。原価を入力して一括アップロードに使えます。"
                        >
                            サンプルフォーマットをダウンロード
                        </button>
                        <button
                            className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                            disabled={importing}
                            onClick={() => fileInputRef.current?.click()}
                            title="CSVを選択して原価を一括登録します"
                        >
                            {importing ? "取込中..." : "一括アップロード"}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void handleImport(f);
                            }}
                        />
                    </div>

                    {/* 2段目: 保存 */}
                    <div className="flex gap-2 flex-wrap justify-end items-center">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || loading || !dirty}
                            className={`px-3 py-1 rounded text-xs text-white ${
                                saving || loading || !dirty
                                    ? "bg-gray-400"
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {saving ? "保存中..." : "保存"}
                        </button>
                    </div>
                </div>
            </div>

            {/* 検索 */}
            <div className="flex items-center gap-2">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPage(1);
                        setKeyword(keywordInput.trim());
                    }}
                    className="flex items-center gap-2"
                >
                    <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        placeholder="SKU / ASIN で検索"
                        className="border px-2 py-1 rounded bg-white text-sm h-8 w-56"
                    />
                    <button
                        type="submit"
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                    >
                        検索
                    </button>
                </form>
            </div>

            {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                    {error}
                </p>
            )}

            {loading ? (
                <LoadingInline />
            ) : (
                <>
                    <MonthlyItemCostTable
                        rows={rows}
                        months={months}
                        onChange={handleChange}
                        avgCostRatesView={avgCostRatesView}
                        onChangeAvgCostRate={handleChangeAvgCostRate}
                        itemCodeHeader="ASIN"
                        skuCodeHeader="SKU"
                    />

                    {/* ページング */}
                    <div className="flex items-center justify-between text-sm text-slate-600">
                        <span>
                            全 {total} 商品（{page} / {totalPages} ページ）
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    if (!confirmDiscardIfDirty()) return;
                                    setPage((p) => Math.max(1, p - 1));
                                }}
                                disabled={page <= 1}
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs disabled:opacity-40"
                            >
                                前へ
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!confirmDiscardIfDirty()) return;
                                    setPage((p) => Math.min(totalPages, p + 1));
                                }}
                                disabled={page >= totalPages}
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs disabled:opacity-40"
                            >
                                次へ
                            </button>
                        </div>
                    </div>
                </>
            )}

            <p className="text-xs text-slate-400">
                ※商品はAmazonの販売実績（SKU）から自動で表示されます。販売前の新商品は表の末尾の空行（黄色）にSKUを入力して登録できます。
                原価は月ごとの登録です（毎月1日に前月の原価が自動で引き継がれます）。商品名の表示は今後対応予定です。
            </p>
        </div>
    );
}
